/* ============================================================================
 * AE Foodstore — Mini-Cart Popup behavior (Build #3 — 2026-05-17)
 * Spec: .cowork-context/project_mini_cart_weee_spec.md
 *
 * Triggers (no fetch/XHR patching — pure observer + event-driven):
 *   - MutationObserver on #Cartcount (bulletproof — cart-card-indicator.js
 *     updates badge after every add path; mutation fires regardless of how
 *     /cart/add was invoked: form POST, jQuery $.ajax, fetch, Shopify Buy SDK)
 *   - `cart:updated` event (dispatched by wbcartdropdown.js + cart-notification.js)
 *   - `cart:item-added` / `product:added-to-cart` / `cart:added` events
 *
 *   Auto-close 4500ms after add (canceled if user hovers popup)
 *   Hover cart icon (desktop) → persistent, mouseleave + 200ms grace → close
 *   Click cart icon → navigate /cart (desktop) / toggle bottom-sheet (mobile)
 *   ESC / click-outside / backdrop tap / mobile swipe-down → close
 *
 * Build #3 changes:
 *   - BUG-D fix: syncCountAndState now also updates #CartTotalText (header
 *     visible cart total) — previously only popup elements updated, header
 *     price stayed stale until page reload.
 *   - BUG-E/F defensive fix: removed window.fetch + XMLHttpRequest.prototype
 *     patches. With Honey extension installed, prototype patches stacked with
 *     extension patches and may have interfered with product-form.js. The
 *     MutationObserver on #Cartcount is sufficient and cannot interfere with
 *     any theme code path.
 *   - P3 sort fix: removed .reverse() — /cart.js in this theme returns items
 *     in latest-first order natively; the previous reverse flipped them to
 *     oldest-top.
 *
 * Rows render: fetched from /cart.js JSON and built client-side.
 * ========================================================================== */
/* ============================================================================
 * P0 HOTFIX (build #5, 2026-05-17) — Stub <cart-notification> custom element.
 *
 * Root cause of BUG-E/F (silent Add To Cart): assets/cart-notification.js is
 * not loaded as a <script> anywhere in the theme, so the <cart-notification>
 * element (rendered by snippets/cart-notification.liquid) is an unknown
 * HTMLElement with no custom methods. product-form.js's onSubmitHandler does:
 *
 *   this.cartNotification = document.querySelector("cart-notification");
 *   ...
 *   this.cartNotification.setActiveElement(document.activeElement);
 *   formData.append("sections", this.cartNotification.getSectionsToRender()...);
 *
 * Both calls throw "not a function" → onSubmitHandler aborts silently → cart
 * never updates → popup never shows. Defining a stub class with the required
 * method surface lets product-form.js proceed; mini-cart-popup keeps its own
 * auto-refresh via MutationObserver on #Cartcount (build #3+) so the popup
 * shows on add via the existing path. renderContents() also dispatches
 * `cart:updated` so listeners (free-shipping-bar, our refresh) see the change.
 *
 * Idempotent: only defines if `cart-notification` isn't already registered.
 * Defined at file load (not inside init/IIFE) so it's available the moment
 * product-form.js constructor runs (mini-cart-popup.js loads before
 * product-form.js per defer document order: header section → layout body).
 * ========================================================================== */
if (window.customElements && !window.customElements.get('cart-notification')) {
  try {
    class AefCartNotificationStub extends HTMLElement {
      setActiveElement(el) {
        this._lastTriggerEl = el;
      }
      getSectionsToRender() {
        return [
          { id: 'cart-notification-product' },
          { id: 'cart-notification-button' },
          { id: 'cart-icon-bubble' }
        ];
      }
      renderContents(parsedState) {
        // Mini-cart-popup auto-refreshes via MutationObserver on #Cartcount +
        // cart:updated event listeners. Dispatch the event so they fire, even
        // if cart-card-indicator.js was the one that updated the badge.
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { source: 'product-form', cart: parsedState || null }
          }));
        } catch (e) {
          var evt = document.createEvent('Event');
          evt.initEvent('cart:updated', true, true);
          document.dispatchEvent(evt);
        }
      }
      open() {}
      close() {}
    }
    window.customElements.define('cart-notification', AefCartNotificationStub);
  } catch (defineErr) {
    if (window.console && console.warn) {
      console.warn('[aef-mini-cart] cart-notification stub define failed:', defineErr);
    }
  }
}

(function () {
  'use strict';
  if (window.__aefMiniCartBound) return;
  window.__aefMiniCartBound = true;

  var MOBILE_BP = 768;
  var HOVER_GRACE_MS = 200;
  var AUTO_CLOSE_MS = 4500;

  var bubble, btn, popup, card, backdrop, closeBtn, countEl, listContainer;
  var hoverTimer = null;
  var autoCloseTimer = null;
  var isOpen = false;
  var lastFocused = null;
  var lastCount = 0;
  var refreshInflight = false;
  var refreshPending = false;
  var pendingShowAdd = false;

  function isMobile() { return window.matchMedia('(max-width: ' + MOBILE_BP + 'px)').matches; }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function sizedImage(url, w) {
    if (!url) return '';
    // Inject _96x suffix before extension for Shopify CDN URLs.
    var clean = url.replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master)\.(jpe?g|png|webp|gif)/i,
      function (_m, ext) { return '_' + w + 'x.' + ext; }
    );
    if (clean === url) {
      clean = url.replace(/\.(jpe?g|png|webp|gif)(\?|$)/i, '_' + w + 'x.$1$2');
    }
    return clean;
  }

  function money(cents) {
    if (cents == null) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  function show(reason) {
    if (!popup) return;
    clearAutoClose();
    if (!isOpen) lastFocused = document.activeElement;
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    // Build #4 mobile polish: bottom-sheet is a true modal on mobile (covers
    // the page with a backdrop, body-scroll-locked). Flip aria-modal so
    // assistive tech treats it as such. On desktop, popup is a hover preview
    // (non-modal), so aria-modal stays false.
    if (isMobile()) {
      document.body.classList.add('aef-mini-cart-open');
      popup.setAttribute('aria-modal', 'true');
    } else {
      popup.setAttribute('aria-modal', 'false');
    }
    isOpen = true;
    if (reason === 'add') {
      autoCloseTimer = setTimeout(hide, AUTO_CLOSE_MS);
    }
  }

  function hide() {
    if (!popup || !isOpen) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('aria-modal', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('aef-mini-cart-open');
    isOpen = false;
    clearAutoClose();
    clearHover();
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function clearAutoClose() { if (autoCloseTimer) { clearTimeout(autoCloseTimer); autoCloseTimer = null; } }
  function clearHover() { if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; } }

  // BUG-E/F defensive (build #4): wrap handlers so a thrown exception from
  // anything inside (or from another listener that ran earlier and left state
  // weird, e.g. Honey extension's product-form errors) cannot kill subsequent
  // listeners or our init flow. Console.warn so issues are still discoverable.
  function safe(fn, label) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[aef-mini-cart] ' + (label || 'handler') + ' error:', e);
        }
      }
    };
  }

  function renderRows(items) {
    if (!listContainer) return;
    if (!items || items.length === 0) {
      listContainer.innerHTML = '';
      return;
    }
    // /cart.js returns items latest-first in this theme — render in array order.
    // BUG-H: each row is an <a> so the whole tile navigates to the PDP.
    var html = '';
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var title = esc(it.product_title || it.title || '');
      var qty = parseInt(it.quantity, 10) || 1;
      var url = esc(it.url || '#');
      var imgRaw = it.image || (it.featured_image && it.featured_image.url) || '';
      var img = imgRaw ? sizedImage(imgRaw, 96) : '';
      var pid = esc(it.product_id || '');
      html += '<a href="' + url + '" class="aef-mini-cart__row" data-product-id="' + pid + '"'
        +    ' aria-label="' + title + ', quantity ' + qty + '">'
        +    '<span class="aef-mini-cart__row-img" aria-hidden="true">'
        +    (img ? '<img src="' + esc(img) + '" alt="" width="48" height="48" loading="lazy" decoding="async">' : '')
        +    '</span>'
        +    '<span class="aef-mini-cart__row-name">' + title + '</span>'
        +    '<span class="aef-mini-cart__row-qty">×' + qty + '</span>'
        +    '</a>';
    }
    listContainer.innerHTML = html;
  }

  function syncCountAndState(count, total) {
    if (!popup) return;
    if (typeof count === 'number') {
      popup.classList.toggle('is-empty', count === 0);
      if (countEl) countEl.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
      // BUG-D fix: also keep header badge in sync (cart-card-indicator.js does
      // this too on /cart/add, but redundant safety covers paths that bypass it).
      // BUG-T (build #9, 2026-05-17): also write data-count attribute so the
      // CSS rule `.aef-cart-badge[data-count="0"] { display: none }` can hide
      // the red "0" badge when the cart is empty.
      var headerBadge = document.getElementById('Cartcount');
      if (headerBadge) {
        headerBadge.textContent = count;
        headerBadge.setAttribute('data-count', String(count));
      }
    }
    if (typeof total === 'number') {
      // BUG-D fix: update the header visible cart total. wbcartdropdown.js syncs
      // this on /cart/change but NOT on /cart/add — leaving header price stale
      // until page reload. Update from every cart refresh.
      var headerTotal = document.getElementById('CartTotalText');
      if (headerTotal) {
        if (window.Shopify && window.Shopify.formatMoney) {
          headerTotal.innerHTML = window.Shopify.formatMoney(
            total, window.Shopify.money_format || '${{amount}}'
          );
        } else {
          headerTotal.textContent = money(total);
        }
      }
    }
  }

  function refreshFromServer(triggerShowOnAdd) {
    if (refreshInflight) {
      refreshPending = true;
      if (triggerShowOnAdd) pendingShowAdd = true;
      return;
    }
    refreshInflight = true;
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var prev = lastCount;
        lastCount = cart.item_count;
        syncCountAndState(cart.item_count, cart.total_price);
        renderRows(cart.items);
        if (triggerShowOnAdd && cart.item_count > prev) {
          show('add');
        }
      })
      .catch(function () {})
      .then(function () {
        refreshInflight = false;
        if (refreshPending) {
          refreshPending = false;
          var s = pendingShowAdd;
          pendingShowAdd = false;
          refreshFromServer(s);
        }
      });
  }

  function init() {
   try {
    bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    popup = bubble.querySelector('[data-aef-mini-cart]');
    btn = bubble.querySelector('#toggleCountCart');
    if (!popup || !btn) return;

    card = popup.querySelector('[data-aef-mini-cart-card]');
    backdrop = popup.querySelector('[data-aef-mini-cart-backdrop]');
    closeBtn = popup.querySelector('[data-aef-mini-cart-close]');
    countEl = popup.querySelector('[data-aef-mini-cart-count]');
    listContainer = popup.querySelector('#webi-main-cart-items .js-contents');

    // Cart icon is now <a href="/cart"> (BUG-L + BUG-G fix). Native click
    // navigates to /cart on both desktop and mobile per spec §1 row 3. No
    // JS click handler needed — hover still triggers popup separately via
    // mouseenter/mouseleave on the parent .aef-cart-wrap (bubble).
    btn.removeAttribute('data-toggle');
    btn.removeAttribute('data-bs-toggle');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'aefMiniCart');

    bubble.addEventListener('mouseenter', safe(function () {
      if (isMobile()) return;
      clearHover();
      clearAutoClose();
      show('hover');
    }, 'bubble.mouseenter'));
    bubble.addEventListener('mouseleave', safe(function () {
      if (isMobile()) return;
      clearHover();
      hoverTimer = setTimeout(hide, HOVER_GRACE_MS);
    }, 'bubble.mouseleave'));
    popup.addEventListener('mouseenter', safe(function () {
      if (isMobile()) return;
      clearHover();
      clearAutoClose();
    }, 'popup.mouseenter'));
    popup.addEventListener('mouseleave', safe(function () {
      if (isMobile()) return;
      clearHover();
      hoverTimer = setTimeout(hide, HOVER_GRACE_MS);
    }, 'popup.mouseleave'));

    // BUG-J fix: click-outside close. Re-query the live #cart-icon-bubble
    // each time in case cart-notification.js section render replaced it,
    // which would leave the captured `bubble` reference orphaned and
    // bubble.contains() returning false for valid in-bubble clicks.
    document.addEventListener('click', safe(function (e) {
      if (!isOpen) return;
      var liveBubble = document.getElementById('cart-icon-bubble') || bubble;
      if (liveBubble && liveBubble.contains(e.target)) return;
      hide();
    }, 'document.click'), true);

    if (backdrop) {
      backdrop.addEventListener('click', function (e) { e.preventDefault(); hide(); });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) { e.preventDefault(); hide(); });
    }
    // BUG-I fix: ESC close. Listen on both keydown and keyup (some testing
    // frameworks dispatch only one), and check isOpen at fire time.
    var escHandler = safe(function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) && isOpen) {
        hide();
      }
    }, 'document.escape');
    document.addEventListener('keydown', escHandler);
    document.addEventListener('keyup', escHandler);

    // Mobile swipe-down close. Build #4 polish: loosened threshold to 40px
    // (was 70) and 800ms window (was 600) so a quick flick from anywhere
    // in the sheet dismisses cleanly. Only triggers when the card is at
    // scrollTop 0 so internal scrolling near the top doesn't double-fire
    // as a dismiss gesture.
    var touchStartY = null;
    var touchStartT = 0;
    if (card) {
      card.addEventListener('touchstart', function (e) {
        if (!isMobile()) return;
        touchStartY = e.touches[0].clientY;
        touchStartT = Date.now();
      }, { passive: true });
      card.addEventListener('touchmove', function (e) {
        if (!isMobile() || touchStartY === null) return;
        var dy = e.touches[0].clientY - touchStartY;
        if (dy > 40 && Date.now() - touchStartT < 800 && card.scrollTop <= 0) {
          hide();
          touchStartY = null;
        }
      }, { passive: true });
      card.addEventListener('touchend', function () { touchStartY = null; });
    }

    // ===== ADD-TO-CART DETECTION (observer + events only — no prototype patching) =====

    // Trigger 1 — MutationObserver on #Cartcount badge.
    // The bulletproof signal: cart-card-indicator.js updates the badge text
    // after every cart mutation regardless of how /cart/add was invoked
    // (form POST, jQuery $.ajax, fetch, Shopify Buy SDK). Cannot interfere
    // with any theme code path because we only OBSERVE — no patching.
    var badge = document.getElementById('Cartcount');
    if (badge && 'MutationObserver' in window) {
      var lastBadge = parseInt((badge.textContent || '0').trim(), 10) || 0;
      var badgeObs = new MutationObserver(function () {
        var newBadge = parseInt((badge.textContent || '0').trim(), 10) || 0;
        // BUG-T (build #9): mirror textContent → data-count so the empty-cart
        // hide CSS rule works regardless of which writer touched the badge
        // (cart-card-indicator.js / wbcartdropdown.js / cart-notification.js /
        // perf-instant.js all set textContent only — they don't know about
        // data-count). Centralising the attribute update here means we don't
        // have to fork-modify every cart writer in the theme.
        if (String(newBadge) !== badge.getAttribute('data-count')) {
          badge.setAttribute('data-count', String(newBadge));
        }
        if (newBadge > lastBadge) {
          refreshFromServer(true);
        } else if (newBadge !== lastBadge) {
          refreshFromServer(false);
        }
        lastBadge = newBadge;
      });
      badgeObs.observe(badge, { childList: true, characterData: true, subtree: true });
    }

    // Trigger 2 — custom events dispatched by theme code paths
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && typeof e.detail.count === 'number') {
        var prev = lastCount;
        var showOnAdd = e.detail.count > prev;
        lastCount = e.detail.count;
        syncCountAndState(e.detail.count, e.detail.total);
        refreshFromServer(showOnAdd);
      } else {
        refreshFromServer(true);
      }
    });
    ['cart:item-added', 'product:added-to-cart', 'cart:added'].forEach(function (ev) {
      document.addEventListener(ev, safe(function () { refreshFromServer(true); }, ev));
    });

    // Initial sync — populates lastCount + renders rows from current cart state
    refreshFromServer(false);
   } catch (e) {
     if (window.console && console.warn) {
       console.warn('[aef-mini-cart] init error — popup will not work, but the rest of the page is unaffected:', e);
     }
   }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
 * BUG-M + BUG-O FIX (build #9, 2026-05-17) — Window-level cart-add queue
 *
 * Build #8 used a module-scoped `var addQueue` inside an IIFE that ran once.
 * That is effectively global, but Cowork's edge-case stress test still saw
 * 5 rapid clicks across 5 DIFFERENT products result in only 2/5 persisting
 * (first + last). Whatever the root cause, Boss prescribed making the queue
 * explicitly window-level (Boss-recommended exact pattern):
 *
 *   window.__aefCartAddQueue = window.__aefCartAddQueue || Promise.resolve();
 *   window.__aefCartAddQueue = window.__aefCartAddQueue.then(function () {
 *     return fetch('/cart/add.js', { method: 'POST', body: formData });
 *   });
 *
 * Why window-level matters beyond style:
 *   - Survives the IIFE bound-guard pattern even if some other script set
 *     `__aefAddQueueBound = true` before us
 *   - Any other future code path (cart drawer, quick-view, etc.) can append
 *     to the same chain so we never have two queues racing in parallel
 *
 * Build #9 also adds a 50 ms post-response settle delay before yielding the
 * chain to the next link. /cart/add.js HTTP 200 returns as soon as Shopify's
 * web tier acks the write, but the cart-cookie session lock can briefly
 * remain held while the change commits. Sequential fetches firing in immediate
 * succession occasionally observed partial loss in earlier QA — the settle
 * delay closes that window. 50 ms × 5 adds = 250 ms total added latency,
 * imperceptible against the popup's 4.5 s auto-close.
 *
 * Background context — product card "+" button is rendered INSIDE .wbimgblock
 * (image container) for absolute positioning, linked to its product-form via
 * HTML5 `form="..."` attribute. resolveForm() walks btn.form (which honours
 * that attribute) so the hijack works regardless of DOM tree position.
 * ========================================================================== */
(function () {
  // Initialize the window-level queue (idempotent). Don't bail out via a
  // bound-guard for the queue itself — the queue is shared state across
  // potentially-multiple loads. Only bail on listener registration.
  window.__aefCartAddQueue = window.__aefCartAddQueue || Promise.resolve();

  if (window.__aefAddQueueListenerBound) return;
  window.__aefAddQueueListenerBound = true;

  var SETTLE_MS = 50; // small delay between sequential /cart/add requests
  var pendingPerBtn = new WeakMap(); // btn → count (for loading state)

  // BUG-Z (2026-05-21) — Global pending counter shared with
  // cart-card-indicator.js. While this is > 0 the indicator's syncFromCart
  // skips overwriting #Cartcount, so the optimistic badge bump below isn't
  // flickered down to the server-truth-at-this-instant in between rapid
  // clicks. The LAST queued add's syncFromCart (which fires after the
  // counter returns to 0) is the one that reconciles the final count.
  if (typeof window.__aefCartAddPending !== 'number') {
    window.__aefCartAddPending = 0;
  }

  function getQtyFromForm(form) {
    if (!form) return 1;
    var q = form.querySelector('input[name="quantity"]');
    if (!q || !q.value) return 1;
    var n = parseInt(q.value, 10);
    return n > 0 ? n : 1;
  }

  function dispatchCartUpdated(line, count, total) {
    var detail = { source: 'aef-add-queue', line: line || null };
    if (typeof count === 'number') detail.count = count;
    if (typeof total === 'number') detail.total = total;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    } catch (e) {
      var evt = document.createEvent('Event');
      evt.initEvent('cart:updated', true, true);
      document.dispatchEvent(evt);
    }
  }

  /* BUG-C (2026-05-29): convert a price text node ("$2.99", "USD 14.95",
     "$1,499.00") to integer cents. Returns 0 if no parseable number found. */
  function parsePriceCents(txt) {
    if (txt == null) return 0;
    var m = String(txt).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    if (!m) return 0;
    return Math.round(parseFloat(m[1]) * 100);
  }

  /* BUG-C: unit price for the line being added, in cents. The card path
     reads the price element inside the surrounding .wbproduct-container;
     the PDP path reads the .mainp-price block in the product section.
     Returns 0 when no price can be found, which causes queueAdd to skip
     the optimistic total update (badge still updates; total catches up on
     the cart-card-indicator reconciliation). */
  function unitPriceCentsFor(btn) {
    if (btn && btn.closest) {
      var card = btn.closest('.wbproduct-container');
      if (card) {
        var p = card.querySelector('.price-item--sale, .price-item--regular');
        if (p) return parsePriceCents(p.textContent);
      }
    }
    var pdp = document.querySelector('.mainp-price .price-item--sale, .mainp-price .price-item--regular');
    if (pdp) return parsePriceCents(pdp.textContent);
    return 0;
  }

  function setLoading(btn, on) {
    if (!btn) return;
    if (on) {
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
    }
  }

  function bumpPending(btn, delta) {
    var n = (pendingPerBtn.get(btn) || 0) + delta;
    pendingPerBtn.set(btn, n);
    if (n > 0) setLoading(btn, true);
    else setLoading(btn, false);
    return n;
  }

  function settle(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Locate the host <form> for a submit button, honouring HTML5 form="..."
  // attribute (button can live outside its form). HTMLButtonElement.form
  // already resolves this for us; fall back to manual id lookup for
  // pathological cases or unattached buttons.
  function resolveForm(btn) {
    if (!btn) return null;
    var form = btn.form || null;
    if (!form) {
      var fid = btn.getAttribute('form');
      if (fid) form = document.getElementById(fid);
    }
    if (!form) form = btn.closest && btn.closest('form');
    return form || null;
  }

  function queueAdd(form, btn) {
    var formData;
    try {
      formData = new FormData(form);
    } catch (e) {
      return; // Form gone or detached — nothing to queue
    }
    // Drop product-form.js-only fields the /cart/add endpoint doesn't want.
    formData.delete('sections');
    formData.delete('sections_url');
    formData.delete('product-id');

    bumpPending(btn, +1);

    // BUG-7 (2026-05-29) — Optimistic header badge bump removed. The "+" quick-
    // add click also fires perf-instant.js's capture-phase listener which
    // independently wrote Cartcount.textContent; both writes racing produced
    // a brief "2" flash on a single click (badge 0 → 1 → 2 → 0 → 1 final).
    // Badge is now server-driven only — via syncCountAndState on the
    // cart:updated event or cart-card-indicator's syncFromCart 200ms after
    // the /cart/add.js response. The pending counter is still incremented
    // so cart-card-indicator skips reconciling mid-queue (rapid 5-click
    // bursts would otherwise have each /cart.js race against the next add).
    var qty = getQtyFromForm(form);
    window.__aefCartAddPending = (window.__aefCartAddPending || 0) + 1;

    // Snapshot the variant id for diagnostic logging — Cowork can correlate
    // expected vs actual when debugging future edge cases.
    var snapshotId = (form.querySelector('input[name="id"]') || {}).value || '?';

    window.__aefCartAddQueue = window.__aefCartAddQueue
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
      })
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          return { ok: r.ok, status: r.status, body: body };
        });
      })
      .then(function (res) {
        if (!res.ok) {
          if (window.console && console.warn) {
            console.warn('[aef-add-queue] /cart/add HTTP ' + res.status +
              ' for variant ' + snapshotId, res.body);
          }
          return null;
        }
        return res.body;
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[aef-add-queue] variant ' + snapshotId + ' failed:', err);
        }
        return null;
      })
      .then(function (line) {
        // BUG-B v2 (2026-05-29): once the server confirms the add via the
        // /cart/add.js response, compute the new badge count as currentBadge
        // + qty (the REQUEST quantity, i.e. what we asked to add) and pass
        // it on the cart:updated event. The listener below picks count out
        // of detail and writes the badge via syncCountAndState immediately
        // — no extra /cart.js round-trip needed. Cuts ~300-500ms off the
        // badge update path that BUG-7 introduced when it removed the pre-
        // response optimistic write. cart-card-indicator's syncFromCart
        // still reconciles after the queue drains.
        //
        // BUG-B v1 used line.quantity here and overshot on rapid bursts:
        // /cart/add.js response.line.quantity is the CUMULATIVE quantity of
        // this variant in cart AFTER the add (e.g. cart had 1 of variant X
        // + add 1 → response shows quantity=2, not the delta of 1). So
        // 5 rapid clicks added 1+2+3+4+5=15 instead of 1+1+1+1+1=5 to the
        // badge across the queue, producing the 1→3→2→5→3→7→4→9→5 dance
        // (each over-count corrected by refreshFromServer). qty is the
        // value we read from the form before posting, which is the actual
        // delta of this request — monotonic and right.
        // BUG-C (2026-05-29): also compute the new header total in cents
        // and pass it on the same cart:updated event so syncCountAndState
        // writes badge + total in the same call — same frame. Earlier,
        // perf-instant wrote CartTotalText optimistically on click and the
        // refresh path overwrote it with the still-empty server total,
        // bouncing $0 ↔ $2.99 six times in 750ms. Total now follows the
        // BUG-B v2 invariant: queue is sequential, so currentTotal at this
        // .then(line) reflects the previous resolve, and curTotal + qty *
        // unitCents is the right delta. unitCents=0 (price not found in
        // DOM) gracefully skips the optimistic total — badge still updates,
        // total catches up on the cart-card-indicator reconciliation.
        var newCount = null;
        var newTotal = null;
        if (line) {
          var badge = document.getElementById('Cartcount');
          var cur = badge ? (parseInt((badge.textContent || '0').trim(), 10) || 0) : 0;
          newCount = cur + qty;

          var totalEl = document.getElementById('CartTotalText');
          var curTotalCents = totalEl ? parsePriceCents(totalEl.textContent) : 0;
          var unitCents = unitPriceCentsFor(btn);
          if (unitCents > 0) {
            newTotal = curTotalCents + qty * unitCents;
          }
        }
        dispatchCartUpdated(line, newCount, newTotal);
        // BUG-7: no optimistic bump to roll back on failure. Badge stays at
        // server truth via the next syncFromCart / refreshFromServer cycle.
        // BUG-O mitigation: brief settle delay so Shopify's cart-cookie
        // session lock fully releases before the next request kicks off.
        return settle(SETTLE_MS);
      })
      .then(function () {
        bumpPending(btn, -1);
        window.__aefCartAddPending =
          Math.max(0, (window.__aefCartAddPending || 1) - 1);
      });
  }

  // Capture-phase click listener — hijack BEFORE product-form's submit
  // handler runs. Resolves the <product-form> host even when the button
  // is rendered outside it via HTML5 form="..." (card__quick-add pattern).
  document.addEventListener('click', function (e) {
    try {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[type="submit"]');
      if (!btn) return;
      var form = resolveForm(btn);
      if (!form) return;
      // Only hijack buttons whose host form is a product-form (or contains
      // an `id` input pointing at a variant). Skip non-cart submit buttons
      // (search forms, footer signup, etc.).
      var isProductForm =
        form.tagName === 'PRODUCT-FORM' ||
        (form.closest && form.closest('product-form')) ||
        form.querySelector('input[name="id"][value]');
      if (!isProductForm) return;
      var variantInput = form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) return;

      // Hijack — prevent default submit; product-form's submit listener
      // never fires because the form submission is canceled at click time.
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      queueAdd(form, btn);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[aef-add-queue] click handler error:', err);
      }
    }
  }, true);
})();
